// Thin MediaRecorder wrapper: getUserMedia → record → Blob. The DOM-bound
// parts live here (untestable in jsdom); the decision logic that IS tested
// lives in pushToTalkModel.ts.

// Browsers disagree on container support; pick the first the platform offers.
// ffmpeg on the sidecar decodes any of these, so the exact pick doesn't matter
// downstream — only that MediaRecorder accepts it.
const PREFERRED_MIME = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
] as const;

function pickMime(): string {
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of PREFERRED_MIME) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

export class MicRecorder {
  private stream?: MediaStream;
  private recorder?: MediaRecorder;
  private chunks: Blob[] = [];
  mimeType = "";

  /** Request the mic and begin capturing. Rejects if permission is denied. */
  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.mimeType = pickMime();
    this.recorder = this.mimeType
      ? new MediaRecorder(this.stream, { mimeType: this.mimeType })
      : new MediaRecorder(this.stream);
    this.chunks = [];
    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    this.recorder.start();
  }

  /** Stop capturing and resolve with the recorded audio as a single Blob. */
  stop(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const rec = this.recorder;
      if (!rec) {
        reject(new Error("recorder not started"));
        return;
      }
      rec.onstop = () => {
        const blob = new Blob(this.chunks, { type: this.mimeType || "audio/webm" });
        this.cleanup();
        resolve(blob);
      };
      try {
        rec.stop();
      } catch (e) {
        this.cleanup();
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  /** Abort without producing a Blob (e.g. on unmount). */
  cancel(): void {
    try {
      this.recorder?.stop();
    } catch {
      // already stopped — nothing to do
    }
    this.cleanup();
  }

  private cleanup(): void {
    for (const track of this.stream?.getTracks() ?? []) {
      track.stop();
    }
    this.stream = undefined;
    this.recorder = undefined;
  }
}

/** A `.webm`/`.ogg`/`.mp4` filename matching the recorded blob's container. */
export function filenameFor(mimeType: string): string {
  if (mimeType.includes("ogg")) return "audio.ogg";
  if (mimeType.includes("mp4")) return "audio.mp4";
  return "audio.webm";
}
